import React, { useEffect, useState } from "react";
import axios from "axios";
import AdminSidebar from "../../components/admin/AdminSidebar";
import { Layers, Plus, Trash2, Edit, X, ArrowRight, Package, Box, SlidersHorizontal, EyeOff, Eye, Check } from "lucide-react";
import { useModal } from "../../context/ModalContext";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function AdminCategories() {
  const { showAlert, showConfirm } = useModal();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals & Forms
  const [catModal, setCatModal] = useState({ show: false, editId: null, name: "" });
  const [brandModal, setBrandModal] = useState({ show: false, category: null, brands: [], name: "" });
  const [modelModal, setModelModal] = useState({ show: false, brand: null, models: [], name: "" });
  const [fieldModal, setFieldModal] = useState({ 
    show: false, 
    category: null, 
    fields: [], 
    brands: [],
    name: "", 
    type: "text",
    is_required: false
  });
  const [editingField, setEditingField] = useState(null);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/categories`);
      setCategories(res.data);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  /* --- CATEGORY LOGIC --- */
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      if (catModal.editId) {
        await axios.put(`${apiUrl}/api/admin/categories/${catModal.editId}`, { name: catModal.name }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${apiUrl}/api/admin/categories`, { name: catModal.name }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      fetchCategories();
      setCatModal({ show: false, editId: null, name: "" });
    } catch (err) {
      showAlert("Error saving category", "error");
    }
  };

  const handleDeleteCategory = async (id) => {
    showConfirm("Delete category? This will fail if it has active posts or brands.", async () => {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      try {
        await axios.delete(`${apiUrl}/api/admin/categories/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchCategories();
      } catch (err) {
        showAlert("Could not delete. Check for associated brands or posts.", "error");
      }
    });
  };

  /* --- BRAND LOGIC --- */
  const openBrands = async (category) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await axios.get(`${apiUrl}/api/admin/brands/${category.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrandModal({ show: true, category, brands: res.data, name: "" });
    } catch (err) {
      showAlert("Failed to fetch brands", "error");
    }
  };

  const handleAddBrand = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await axios.post(`${apiUrl}/api/admin/brands`, { 
        name: brandModal.name, 
        category_id: brandModal.category.id 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrandModal({ ...brandModal, brands: [...brandModal.brands, res.data], name: "" });
    } catch (err) {
      showAlert("Error adding brand", "error");
    }
  };

  const handleDeleteBrand = async (id) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      await axios.delete(`${apiUrl}/api/admin/brands/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrandModal({ ...brandModal, brands: brandModal.brands.filter(b => b.id !== id) });
    } catch (err) {
      showAlert("Error deleting brand", "error");
    }
  };

  /* --- MODEL LOGIC --- */
  const openModels = async (brand) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await axios.get(`${apiUrl}/api/admin/models/${brand.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModelModal({ show: true, brand, models: res.data, name: "" });
    } catch (err) {
      showAlert("Failed to fetch models", "error");
    }
  };

  const handleAddModel = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await axios.post(`${apiUrl}/api/admin/models`, { 
        name: modelModal.name, 
        brand_id: modelModal.brand.id 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModelModal({ ...modelModal, models: [...modelModal.models, res.data], name: "" });
    } catch (err) {
      showAlert("Error adding model", "error");
    }
  };

  const handleDeleteModel = async (id) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      await axios.delete(`${apiUrl}/api/admin/models/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModelModal({ ...modelModal, models: modelModal.models.filter(m => m.id !== id) });
    } catch (err) {
      showAlert("Error deleting model", "error");
    }
  };

  /* --- FIELD LOGIC --- */
  const openFields = async (category) => {
    try {
      const [fieldsRes, brandsRes] = await Promise.all([
        axios.get(`${apiUrl}/api/categories/${category.id}/fields`),
        axios.get(`${apiUrl}/api/brands/${category.id}`).catch(() => ({ data: [] }))
      ]);
      setFieldModal({
        show: true,
        category,
        fields: fieldsRes.data,
        brands: brandsRes.data || [],
        name: "",
        type: "text"
      });
      setEditingField(null);
    } catch (err) {
      showAlert("Failed to fetch fields", "error");
    }
  };

  const handleAddField = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await axios.post(`${apiUrl}/api/admin/fields`, {
        category_id: fieldModal.category.id,
        name: fieldModal.name,
        type: fieldModal.type,
        is_required: fieldModal.is_required ? 1 : 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setFieldModal({ 
        ...fieldModal, 
        fields: [...fieldModal.fields, { ...res.data, is_required: Boolean(fieldModal.is_required), options: [], rules: { hideForBrand: [], onlyForBrand: [] } }], 
        name: "", 
        type: "text",
        is_required: false
      });
    } catch (err) {
      showAlert("Error adding field", "error");
    }
  };

  const handleDeleteField = async (fieldId) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      await axios.delete(`${apiUrl}/api/admin/fields/${fieldId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFieldModal({ ...fieldModal, fields: fieldModal.fields.filter(f => f.id !== fieldId) });
    } catch (err) {
      showAlert("Error deleting field", "error");
    }
  };

  const handleAddOption = async (fieldId, value) => {
    if (!value) return;
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      await axios.post(`${apiUrl}/api/admin/fields/${fieldId}/options`, { value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh fields
      const res = await axios.get(`${apiUrl}/api/categories/${fieldModal.category.id}/fields`);
      setFieldModal({ ...fieldModal, fields: res.data });
    } catch (err) {
      showAlert("Error adding option", "error");
    }
  };

  const handleDeleteOption = async (optionId) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      // Note: Backend endpoint for delete option uses option ID
      // But my local frontend state doesn't have option IDs easily accessible without refreshing
      // So I'll just refresh after delete
      await axios.delete(`${apiUrl}/api/admin/options/${optionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const res = await axios.get(`${apiUrl}/api/categories/${fieldModal.category.id}/fields`);
      setFieldModal({ ...fieldModal, fields: res.data });
    } catch (err) {
      showAlert("Error deleting option", "error");
    }
  };

  const handleUpdateField = async (fieldId, name, type, is_required) => {
    if (!name || !name.trim()) return;
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      await axios.put(
        `${apiUrl}/api/admin/fields/${fieldId}`,
        { name: name.trim(), type, is_required: is_required ? 1 : 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const res = await axios.get(`${apiUrl}/api/categories/${fieldModal.category.id}/fields`);
      setFieldModal((prev) => ({ ...prev, fields: res.data }));
      setEditingField(null);
      showAlert("Field updated successfully", "success");
    } catch (err) {
      showAlert("Error updating field", "error");
    }
  };

  const handleAddRule = async (fieldId, ruleType, brandName) => {
    if (!brandName || !brandName.trim()) {
      showAlert("Please enter or select a brand name", "error");
      return;
    }
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      await axios.post(
        `${apiUrl}/api/admin/fields/${fieldId}/rules`,
        { rule_type: ruleType, brand_name: brandName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const res = await axios.get(`${apiUrl}/api/categories/${fieldModal.category.id}/fields`);
      setFieldModal((prev) => ({ ...prev, fields: res.data }));
      showAlert("Brand rule added successfully", "success");
    } catch (err) {
      showAlert("Error adding rule", "error");
    }
  };

  const handleDeleteRule = async (ruleId) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      await axios.delete(`${apiUrl}/api/admin/rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const res = await axios.get(`${apiUrl}/api/categories/${fieldModal.category.id}/fields`);
      setFieldModal((prev) => ({ ...prev, fields: res.data }));
      showAlert("Brand rule removed", "success");
    } catch (err) {
      showAlert("Error deleting rule", "error");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <main className="ml-64 flex-grow p-8">
        <header className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Categories & Structure</h1>
            <p className="text-gray-500">Manage site categories, brands, and models.</p>
          </div>
          <button 
            onClick={() => setCatModal({ show: true, editId: null, name: "" })}
            className="bg-green-600 text-white px-6 py-3 rounded-2xl flex items-center gap-2 hover:bg-green-700 transition-all shadow-lg shadow-green-100 font-bold"
          >
            <Plus size={20} />
            New Category
          </button>
        </header>

        {loading ? (
          <div className="text-gray-500">Loading structure...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((cat) => (
              <div key={cat.id} className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="flex justify-between items-start mb-4">
                  <div className="p-4 bg-green-50 text-green-600 rounded-2xl">
                    <Layers size={28} />
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setCatModal({ show: true, editId: cat.id, name: cat.name })}
                      className="p-2 bg-gray-50 text-gray-400 rounded-xl hover:bg-gray-100 hover:text-gray-900 transition-colors"
                    >
                      <Edit size={18} />
                    </button>
                    <button 
                      onClick={() => handleDeleteCategory(cat.id)}
                      className="p-2 bg-red-50 text-red-400 rounded-xl hover:bg-red-100 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{cat.name}</h3>
                
                <div className="space-y-3">
                   <button 
                    onClick={() => openBrands(cat)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-2xl group/btn hover:bg-green-600 hover:text-white transition-all font-bold text-gray-700"
                   >
                     <div className="flex items-center gap-3">
                        <Package size={18} />
                        Manage Brands
                     </div>
                     <ArrowRight size={18} className="group-hover/btn:translate-x-1 transition-transform" />
                   </button>
                   <button 
                    onClick={() => openFields(cat)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-2xl group/btn hover:bg-blue-600 hover:text-white transition-all font-bold text-gray-700"
                   >
                     <div className="flex items-center gap-3">
                        <Box size={18} />
                        Custom Fields
                     </div>
                     <ArrowRight size={18} className="group-hover/btn:translate-x-1 transition-transform" />
                   </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* --- CATEGORY MODAL --- */}
        {catModal.show && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <form onSubmit={handleSaveCategory} className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {catModal.editId ? "Edit Category" : "New Category"}
              </h2>
              <input 
                autoFocus
                type="text"
                required
                value={catModal.name}
                onChange={(e) => setCatModal({ ...catModal, name: e.target.value })}
                placeholder="Category Name (e.g., Electronics)"
                className="w-full px-5 py-4 rounded-2xl bg-gray-50 border-transparent focus:bg-white focus:border-green-500 focus:ring-0 transition-all text-lg mb-6 outline-none shadow-inner"
              />
              <div className="flex gap-3">
                <button 
                  type="button"
                  onClick={() => setCatModal({ show: false, editId: null, name: "" })}
                  className="flex-1 py-4 bg-gray-100 text-gray-700 font-bold rounded-2xl hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 py-4 bg-green-600 text-white font-bold rounded-2xl hover:bg-green-700 transition-all shadow-lg shadow-green-100"
                >
                  {catModal.editId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* --- BRAND MODAL --- */}
        {brandModal.show && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
            <div className="bg-white rounded-[2.5rem] max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div className="p-8 border-b flex justify-between items-center bg-gray-50/50">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Manage Brands</h2>
                  <p className="text-green-600 font-bold text-sm uppercase tracking-wider">{brandModal.category.name}</p>
                </div>
                <button 
                  onClick={() => setBrandModal({ show: false, category: null, brands: [], name: "" })}
                  className="p-3 hover:bg-gray-200 rounded-2xl transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex-grow overflow-y-auto p-8">
                <form onSubmit={handleAddBrand} className="flex gap-3 mb-8">
                  <input 
                    type="text"
                    required
                    value={brandModal.name}
                    onChange={(e) => setBrandModal({ ...brandModal, name: e.target.value })}
                    placeholder="New Brand (e.g., Apple)"
                    className="flex-1 px-5 py-4 rounded-2xl bg-gray-100 border-transparent focus:bg-white focus:border-green-500 focus:ring-0 transition-all outline-none"
                  />
                  <button type="submit" className="px-8 py-4 bg-green-600 text-white font-bold rounded-2xl hover:bg-green-700 shadow-lg shadow-green-100">
                    Add
                  </button>
                </form>

                <div className="grid grid-cols-2 gap-4">
                   {brandModal.brands.map(brand => (
                     <div key={brand.id} className="group p-4 bg-white border border-gray-100 rounded-2xl flex items-center justify-between hover:border-green-200 hover:shadow-sm transition-all">
                       <span className="font-bold text-gray-700">{brand.name}</span>
                       <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                         <button 
                          onClick={() => openModels(brand)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                         >
                           <ArrowRight size={16} />
                         </button>
                         <button 
                          onClick={() => handleDeleteBrand(brand.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                         >
                           <Trash2 size={16} />
                         </button>
                       </div>
                     </div>
                   ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- MODEL MODAL --- */}
        {modelModal.show && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
            <div className="bg-white rounded-[2.5rem] max-w-xl w-full max-h-[70vh] overflow-hidden shadow-2xl flex flex-col animate-in fade-in zoom-in duration-300">
              <div className="p-8 border-b flex justify-between items-center bg-blue-50/30">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Manage Models</h2>
                  <p className="text-blue-600 font-bold text-sm uppercase tracking-wider">{modelModal.brand.name}</p>
                </div>
                <button 
                  onClick={() => setModelModal({ show: false, brand: null, models: [], name: "" })}
                  className="p-3 hover:bg-gray-200 rounded-2xl transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex-grow overflow-y-auto p-8">
                <form onSubmit={handleAddModel} className="flex gap-3 mb-6">
                  <input 
                    type="text"
                    required
                    value={modelModal.name}
                    onChange={(e) => setModelModal({ ...modelModal, name: e.target.value })}
                    placeholder="New Model (e.g., iPhone 15)"
                    className="flex-1 px-5 py-4 rounded-2xl bg-gray-100 border-transparent focus:bg-white focus:border-blue-500 focus:ring-0 transition-all outline-none"
                  />
                  <button type="submit" className="px-8 py-4 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 shadow-lg shadow-blue-100">
                    Add
                  </button>
                </form>

                <div className="space-y-2">
                   {modelModal.models.map(model => (
                     <div key={model.id} className="group p-4 bg-white border border-gray-50 rounded-2xl flex items-center justify-between hover:bg-gray-50 transition-all">
                       <span className="font-bold text-gray-700">{model.name}</span>
                       <button 
                        onClick={() => handleDeleteModel(model.id)}
                        className="p-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all"
                       >
                         <Trash2 size={16} />
                       </button>
                     </div>
                   ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- FIELD MODAL --- */}
        {fieldModal.show && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
            <div className="bg-white rounded-[2.5rem] max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div className="p-8 border-b flex justify-between items-center bg-gray-50/50">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Custom Fields</h2>
                  <p className="text-blue-600 font-bold text-sm uppercase tracking-wider">{fieldModal.category.name}</p>
                </div>
                <button 
                  onClick={() => setFieldModal({ ...fieldModal, show: false })}
                  className="p-3 hover:bg-gray-200 rounded-2xl transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex-grow overflow-y-auto p-8 bg-gray-50/30">
                {/* Add New Field */}
                <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm mb-8">
                  <h3 className="font-bold text-gray-900 mb-4">Add New Attribute</h3>
                  <form onSubmit={handleAddField} className="flex flex-wrap gap-3">
                    <input 
                      type="text"
                      required
                      value={fieldModal.name}
                      onChange={(e) => setFieldModal({ ...fieldModal, name: e.target.value })}
                      placeholder="Field Name (e.g., RAM)"
                      className="flex-1 min-w-[200px] px-5 py-4 rounded-2xl bg-gray-50 border-transparent focus:bg-white focus:border-blue-500 transition-all outline-none"
                    />
                    <select 
                      value={fieldModal.type}
                      onChange={(e) => setFieldModal({ ...fieldModal, type: e.target.value })}
                      className="px-5 py-4 rounded-2xl bg-gray-50 border-transparent focus:bg-white focus:border-blue-500 transition-all outline-none font-bold text-gray-600"
                    >
                      <option value="text">Text Input</option>
                      <option value="select">Dropdown Menu</option>
                    </select>
                    <label className="flex items-center gap-2 cursor-pointer select-none px-5 py-4 rounded-2xl bg-gray-50 border border-gray-100 hover:bg-gray-100 transition">
                      <input
                        type="checkbox"
                        checked={!!fieldModal.is_required}
                        onChange={(e) => setFieldModal({ ...fieldModal, is_required: e.target.checked })}
                        className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="text-sm font-bold text-gray-700">Required</span>
                    </label>
                    <button type="submit" className="px-8 py-4 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all">
                      Add Field
                    </button>
                  </form>
                </div>

                {/* Fields List */}
                <div className="space-y-6">
                   {fieldModal.fields.map(field => (
                     <div key={field.id} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:border-blue-200 transition-all">
                       <div className="flex justify-between items-center mb-5 pb-4 border-b border-gray-50">
                         <div className="flex items-center gap-3 flex-1">
                           <div className={`p-3 rounded-xl shrink-0 ${field.type === 'select' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'}`}>
                             <Box size={20} />
                           </div>
                           {editingField?.id === field.id ? (
                             <div className="flex flex-wrap items-center gap-2 flex-1">
                               <input
                                 type="text"
                                 value={editingField.name}
                                 onChange={(e) => setEditingField({ ...editingField, name: e.target.value })}
                                 className="px-3 py-1.5 border border-blue-400 rounded-xl text-sm font-bold text-gray-900 outline-none"
                                 placeholder="Field name"
                               />
                               <select
                                 value={editingField.type}
                                 onChange={(e) => setEditingField({ ...editingField, type: e.target.value })}
                                 className="px-2 py-1.5 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 outline-none"
                               >
                                 <option value="text">Text Input</option>
                                 <option value="select">Dropdown Menu</option>
                               </select>
                               <label className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 rounded-xl border border-gray-200 text-xs font-bold text-gray-700 cursor-pointer">
                                 <input
                                   type="checkbox"
                                   checked={!!editingField.is_required}
                                   onChange={(e) => setEditingField({ ...editingField, is_required: e.target.checked })}
                                   className="w-3.5 h-3.5 rounded text-blue-600 cursor-pointer"
                                 />
                                 <span>Required</span>
                               </label>
                               <button
                                 type="button"
                                 onClick={() => handleUpdateField(field.id, editingField.name, editingField.type, editingField.is_required)}
                                 className="p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition cursor-pointer"
                                 title="Save changes"
                               >
                                 <Check size={16} />
                               </button>
                               <button
                                 type="button"
                                 onClick={() => setEditingField(null)}
                                 className="p-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition cursor-pointer"
                                 title="Cancel"
                               >
                                 <X size={16} />
                               </button>
                             </div>
                           ) : (
                             <div>
                               <div className="flex items-center gap-2">
                                 <h4 className="font-black text-gray-900">{field.name}</h4>
                                 {field.is_required ? (
                                   <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-200 uppercase tracking-wider">
                                     Required
                                   </span>
                                 ) : (
                                   <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-gray-100 text-gray-500 uppercase tracking-wider">
                                     Optional
                                   </span>
                                 )}
                               </div>
                               <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">{field.type} field</p>
                             </div>
                           )}
                         </div>
                         <div className="flex items-center gap-1 shrink-0">
                           {editingField?.id !== field.id && (
                             <button
                               onClick={() => setEditingField({ id: field.id, name: field.name, type: field.type, is_required: Boolean(field.is_required) })}
                               className="p-2 text-gray-400 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-all cursor-pointer"
                               title="Edit field"
                             >
                               <Edit size={18} />
                             </button>
                           )}
                           <button 
                            onClick={() => handleDeleteField(field.id)}
                            className="p-2 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all cursor-pointer"
                            title="Delete field"
                           >
                             <Trash2 size={18} />
                           </button>
                         </div>
                       </div>

                       {field.type === 'select' && (
                         <div className="space-y-4 mb-4">
                           <div className="flex gap-2">
                             <input 
                               type="text"
                               placeholder="Add option (press Enter)..."
                               onKeyDown={(e) => {
                                 if (e.key === 'Enter') {
                                   handleAddOption(field.id, e.target.value);
                                   e.target.value = '';
                                 }
                               }}
                               className="flex-1 px-4 py-2 bg-gray-50 rounded-xl text-sm outline-none focus:bg-white border-transparent focus:border-purple-200 border-2 transition-all"
                             />
                           </div>
                           <div className="flex flex-wrap gap-2">
                              {field.options?.map((opt) => (
                                <span key={opt.id} className="group px-3 py-1.5 bg-purple-50 text-purple-700 rounded-xl text-xs font-bold flex items-center gap-2 border border-purple-100">
                                  {opt.value}
                                  <button 
                                    onClick={() => handleDeleteOption(opt.id)}
                                    className="hover:text-red-600 transition-colors"
                                  >
                                    <X size={12} />
                                  </button>
                                </span>
                              ))}
                           </div>
                         </div>
                       )}

                       {/* Brand Visibility Rules Manager */}
                       <FieldRuleManager
                         field={field}
                         brands={fieldModal.brands || []}
                         onAddRule={handleAddRule}
                         onDeleteRule={handleDeleteRule}
                       />
                     </div>
                   ))}
                </div>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

function FieldRuleManager({ field, brands, onAddRule, onDeleteRule }) {
  const [ruleType, setRuleType] = useState("hideForBrand");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [customBrand, setCustomBrand] = useState("");

  const handleAdd = (e) => {
    e.preventDefault();
    const brandToUse = selectedBrand === "__custom__" ? customBrand : selectedBrand;
    if (!brandToUse || !brandToUse.trim()) return;
    onAddRule(field.id, ruleType, brandToUse.trim());
    setSelectedBrand("");
    setCustomBrand("");
  };

  const hasRules = (field.rules?.hideForBrand?.length || 0) + (field.rules?.onlyForBrand?.length || 0) > 0;

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-500">
          <SlidersHorizontal size={14} className="text-gray-400" />
          <span>Brand Visibility Rules</span>
        </div>
        <span className="text-[11px] text-gray-400">
          {field.rules?.hideForBrand?.length
            ? `Hidden for ${field.rules.hideForBrand.length} brand(s)`
            : field.rules?.onlyForBrand?.length
            ? `Only visible for ${field.rules.onlyForBrand.length} brand(s)`
            : "Visible for all brands"}
        </span>
      </div>

      {/* Existing Rules Pills */}
      <div className="flex flex-wrap gap-2 mb-3">
        {field.rules?.hideForBrand?.map((r) => (
          <span
            key={r.id || `hide-${r.brand_name}`}
            className="px-2.5 py-1 bg-red-50 text-red-700 border border-red-200 rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-xs"
          >
            <EyeOff size={12} className="text-red-500" />
            <span>Hide for <strong>{r.brand_name}</strong></span>
            <button
              type="button"
              onClick={() => onDeleteRule(r.id)}
              className="ml-1 text-red-400 hover:text-red-700 p-0.5 rounded transition cursor-pointer"
              title="Delete rule"
            >
              <X size={12} />
            </button>
          </span>
        ))}

        {field.rules?.onlyForBrand?.map((r) => (
          <span
            key={r.id || `only-${r.brand_name}`}
            className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-xs"
          >
            <Eye size={12} className="text-blue-500" />
            <span>Only for <strong>{r.brand_name}</strong></span>
            <button
              type="button"
              onClick={() => onDeleteRule(r.id)}
              className="ml-1 text-blue-400 hover:text-blue-700 p-0.5 rounded transition cursor-pointer"
              title="Delete rule"
            >
              <X size={12} />
            </button>
          </span>
        ))}

        {!hasRules && (
          <p className="text-xs text-gray-400 italic py-0.5">
            No brand restrictions (visible for all brands).
          </p>
        )}
      </div>

      {/* Add New Rule Inline Form */}
      <form onSubmit={handleAdd} className="flex flex-wrap items-center gap-2 pt-1">
        <select
          value={ruleType}
          onChange={(e) => setRuleType(e.target.value)}
          className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 outline-none focus:bg-white focus:border-blue-400 transition"
        >
          <option value="hideForBrand">🚫 Hide for brand</option>
          <option value="onlyForBrand">👁️ Only for brand</option>
        </select>

        {brands.length > 0 && selectedBrand !== "__custom__" ? (
          <select
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-700 outline-none focus:bg-white focus:border-blue-400 transition min-w-[140px]"
          >
            <option value="">Select brand...</option>
            {brands.map((b) => (
              <option key={b.id} value={b.name}>
                {b.name}
              </option>
            ))}
            <option value="__custom__">+ Custom brand name...</option>
          </select>
        ) : (
          <div className="flex items-center gap-1 flex-1 min-w-[140px]">
            <input
              type="text"
              placeholder="Brand name (e.g. Apple)..."
              value={selectedBrand === "__custom__" ? customBrand : selectedBrand}
              onChange={(e) =>
                selectedBrand === "__custom__"
                  ? setCustomBrand(e.target.value)
                  : setSelectedBrand(e.target.value)
              }
              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-700 outline-none focus:bg-white focus:border-blue-400 transition"
            />
            {brands.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setSelectedBrand("");
                  setCustomBrand("");
                }}
                className="text-xs text-blue-600 hover:underline px-1 whitespace-nowrap"
              >
                Choose list
              </button>
            )}
          </div>
        )}

        <button
          type="submit"
          className="px-4 py-2 bg-gray-900 text-white rounded-xl text-xs font-bold hover:bg-gray-800 transition shadow-sm cursor-pointer whitespace-nowrap"
        >
          + Add Rule
        </button>
      </form>
    </div>
  );
}
